import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';
import DocumentUpload from './DocumentUpload';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';

const EventEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    eventType: 'other',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    venue: '',
    organizer: {
      department: '',
      contactPerson: '',
      email: '',
      phone: ''
    },
    registrationRequired: false,
    registrationDeadline: '',
    maxParticipants: '',
    fees: {
      students: 0,
      faculty: 0,
      external: 0
    },
    images: [],
    documents: [],
    tags: [],
    status: 'draft',
    featured: false
  });

  const [newTag, setNewTag] = useState('');
  const [newImage, setNewImage] = useState('');
  const [newDocument, setNewDocument] = useState({ filename: '', url: '', fileType: '' });

  useEffect(() => {
    if (isEditing) {
      fetchEvent();
    }
  }, [id, isEditing]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEvent(id);
      const event = response.data.event;
      
      setFormData({
        ...event,
        startDate: event.startDate ? new Date(event.startDate).toISOString().split('T')[0] : '',
        endDate: event.endDate ? new Date(event.endDate).toISOString().split('T')[0] : '',
        registrationDeadline: event.registrationDeadline ? new Date(event.registrationDeadline).toISOString().split('T')[0] : '',
        organizer: event.organizer || { department: '', contactPerson: '', email: '', phone: '' },
        fees: event.fees || { students: 0, faculty: 0, external: 0 },
        images: event.images || [],
        documents: event.documents || [],
        tags: event.tags || []
      });
    } catch (error) {
      setError('Failed to load event data');
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addImage = () => {
    if (newImage.trim() && !formData.images.includes(newImage.trim())) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage.trim()]
      }));
      setNewImage('');
    }
  };

  const removeImage = (imageToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imageToRemove)
    }));
  };

  const addDocument = () => {
    if (newDocument.filename.trim() && newDocument.url.trim()) {
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, { ...newDocument }]
      }));
      setNewDocument({ filename: '', url: '', fileType: '' });
    }
  };

  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isEditing) {
        await apiService.updateEvent(id, formData);
        setSuccess('Event updated successfully!');
      } else {
        await apiService.createEvent(formData);
        setSuccess('Event created successfully!');
        setTimeout(() => {
          navigate('/admin/events');
        }, 1500);
      }
    } catch (error) {
      setError('Failed to save event');
      console.error('Error saving event:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading event data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-sans font-bold text-slate mb-2">
            {isEditing ? 'Edit Event' : 'Create New Event'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Update event information and settings.' : 'Create a new event for the college community.'}
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/events')}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-slate rounded-lg flex items-center gap-2"
        >
          <i className="fas fa-arrow-left"></i>
          Back to Events
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Event Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Enter event title"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Event Type *</label>
                <select
                  value={formData.eventType}
                  onChange={(e) => handleInputChange('eventType', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  required
                >
                  <option value="seminar">Seminar</option>
                  <option value="workshop">Workshop</option>
                  <option value="conference">Conference</option>
                  <option value="cultural">Cultural</option>
                  <option value="sports">Sports</option>
                  <option value="technical">Technical</option>
                  <option value="academic">Academic</option>
                  <option value="placement">Placement</option>
                  <option value="orientation">Orientation</option>
                  <option value="graduation">Graduation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-2">Venue *</label>
                <input
                  type="text"
                  value={formData.venue}
                  onChange={(e) => handleInputChange('venue', e.target.value)}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                  placeholder="Event venue"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Short Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={3}
                placeholder="Brief description of the event"
                required
              />
            </div>
          </div>
        </div>

        {/* Date and Time */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Date & Time</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">End Date *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Start Time</label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">End Time</label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Event Content</h2>
          
          <div>
            <label className="block text-sm font-medium text-slate mb-2">Detailed Content *</label>
            <EnhancedRichTextEditor
              value={formData.content}
              onChange={(value) => handleInputChange('content', value)}
              placeholder="Enter detailed event content, agenda, speakers, etc..."
            />
          </div>
        </div>

        {/* Organizer Information */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Organizer Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Department</label>
              <input
                type="text"
                value={formData.organizer.department}
                onChange={(e) => handleInputChange('organizer.department', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Organizing department"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Contact Person</label>
              <input
                type="text"
                value={formData.organizer.contactPerson}
                onChange={(e) => handleInputChange('organizer.contactPerson', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Contact person name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Email</label>
              <input
                type="email"
                value={formData.organizer.email}
                onChange={(e) => handleInputChange('organizer.email', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="contact@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Phone</label>
              <input
                type="tel"
                value={formData.organizer.phone}
                onChange={(e) => handleInputChange('organizer.phone', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="+91 98765-43210"
              />
            </div>
          </div>
        </div>

        {/* Registration & Fees */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Registration & Fees</h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="registrationRequired"
                checked={formData.registrationRequired}
                onChange={(e) => handleInputChange('registrationRequired', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="registrationRequired" className="text-sm font-medium text-slate">
                Registration Required
              </label>
            </div>

            {formData.registrationRequired && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate mb-2">Registration Deadline</label>
                    <input
                      type="date"
                      value={formData.registrationDeadline}
                      onChange={(e) => handleInputChange('registrationDeadline', e.target.value)}
                      className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate mb-2">Max Participants</label>
                    <input
                      type="number"
                      value={formData.maxParticipants}
                      onChange={(e) => handleInputChange('maxParticipants', parseInt(e.target.value) || '')}
                      className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                      placeholder="Maximum number of participants"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate mb-2">Registration Fees</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Students</label>
                      <input
                        type="number"
                        value={formData.fees.students}
                        onChange={(e) => handleInputChange('fees.students', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Faculty</label>
                      <input
                        type="number"
                        value={formData.fees.faculty}
                        onChange={(e) => handleInputChange('fees.faculty', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">External</label>
                      <input
                        type="number"
                        value={formData.fees.external}
                        onChange={(e) => handleInputChange('fees.external', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Media & Documents */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Images & Documents</h2>
          
          <div className="space-y-6">
            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Event Images</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newImage}
                    onChange={(e) => setNewImage(e.target.value)}
                    className="flex-1 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="Image URL"
                  />
                  <button
                    type="button"
                    onClick={addImage}
                    className="px-4 py-2 bg-crimson text-white rounded hover:bg-crimson/90"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-1">
                  {formData.images.map((image, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-sm truncate">{image}</span>
                      <button
                        type="button"
                        onClick={() => removeImage(image)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Documents */}
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Event Documents</label>
              <div className="space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input
                    type="text"
                    value={newDocument.filename}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, filename: e.target.value }))}
                    className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="File name"
                  />
                  <input
                    type="url"
                    value={newDocument.url}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, url: e.target.value }))}
                    className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="Document URL"
                  />
                  <input
                    type="text"
                    value={newDocument.fileType}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, fileType: e.target.value }))}
                    className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="File type (PDF, DOC, etc.)"
                  />
                  <button
                    type="button"
                    onClick={addDocument}
                    className="px-4 py-2 bg-crimson text-white rounded hover:bg-crimson/90"
                  >
                    Add
                  </button>
                </div>
                <div className="space-y-1">
                  {formData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex-1">
                        <span className="text-sm font-medium">{doc.filename}</span>
                        <span className="text-xs text-gray-500 ml-2">({doc.fileType})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Tags</h2>
          
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Add tags to help categorize this event"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-crimson text-white rounded hover:bg-crimson/90"
              >
                Add Tag
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-2 text-gray-500 hover:text-red-500"
                  >
                    <i className="fas fa-times text-xs"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Event Settings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            <div className="flex items-center pt-8">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => handleInputChange('featured', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="featured" className="text-sm font-medium text-slate">
                Mark as Featured Event
              </label>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/events')}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-slate rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                {isEditing ? 'Update Event' : 'Create Event'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventEdit;